import { StaticInterface } from '../../../helpers/static-interface';
import { debounce } from '@/helpers/events';

export interface ITpxNavigationBarUtils {
}

export interface IStaticTpxNavigationBarUtils {
  new(...args: any[]): ITpxNavigationBarUtils;
  init(): void;
}

/**
 * NavigationBar utilities
 * @class
 */
export default class TpxNavigationBarUtils implements StaticInterface<IStaticTpxNavigationBarUtils, typeof TpxNavigationBarUtils> {
  element: HTMLElement;
  mobileNav: HTMLButtonElement | null;
  mobileHamburgerBtn: HTMLButtonElement | null;
  mobileCloseBtn: HTMLButtonElement | null;
  mobileDialog: HTMLDialogElement | null;
  mobileDropdownBtns: NodeListOf<Element>;
  mobileDropdownBackBtns: NodeListOf<Element>;
  mobileDropdownCloseBtns: NodeListOf<Element>;
  mobileDropdownDialogs: NodeListOf<Element>;
  desktopNav: HTMLButtonElement | null;
  desktopDropdownBtns: NodeListOf<Element>;
  desktopDropdownDialogs: NodeListOf<Element>;
  desktopFooter: NodeListOf<Element>;
  lastScrollTop: number;
  viewportWidth: number;

  constructor(element: HTMLElement) {
    this.element = element;
    this.mobileNav = element.querySelector("[data-component='navigation-bar-mobile']");
    this.mobileHamburgerBtn = element.querySelector("[data-component='mobile-hamburger-button']");
    this.mobileCloseBtn = element.querySelector("[data-component='mobile-close-button']");
    this.mobileDialog = element.querySelector("[data-component='mobile-dialog']");
    this.mobileDropdownBtns = element.querySelectorAll("[data-component='mobile-dropdown-button']");
    this.mobileDropdownBackBtns = element.querySelectorAll("[data-component='mobile-dropdown-back-button']");
    this.mobileDropdownCloseBtns = element.querySelectorAll("[data-component='mobile-dropdown-close-button']");
    this.mobileDropdownDialogs = element.querySelectorAll("[data-component='mobile-dropdown-dialog']");
    this.desktopNav = element.querySelector("[data-component='navigation-bar-desktop']");
    this.desktopDropdownBtns = element.querySelectorAll("[data-component='desktop-dropdown-button']");
    this.desktopDropdownDialogs = element.querySelectorAll("[data-component='desktop-dropdown-dialog']");
    this.desktopFooter = element.querySelectorAll("[data-component='navigation-bar-desktop-footer']");
    this.lastScrollTop = 0;
    this.viewportWidth = window.innerWidth;

    const removeFocus = (dialog: HTMLDialogElement) => {
      const buttons = dialog.querySelectorAll("button");
      const links = dialog.querySelectorAll("a");

      //prevent the first element that can be focused within the dialog automatically receiving focus
      buttons.forEach((btn) => {
        btn.blur();
      })

      links.forEach((link) => {
        link.blur();
      })
    }

    const openMobileDialog = () => {
      if (this.mobileDialog) {
        this.mobileDialog.showModal();
        removeFocus(this.mobileDialog);
      }
    }

    const closeMobileDialog = () => {
      if (this.mobileDialog) {
        this.mobileDialog.close();
      }
    }

    const openMobileDropdownDialog = (e) => {
      const clickedNavItem = e.target.closest("[data-component='mobile-dropdown-nav-item']");
      const mobileDropdownDialog = clickedNavItem.querySelector("[data-component='mobile-dropdown-dialog']");
      mobileDropdownDialog.showModal();
      removeFocus(mobileDropdownDialog);
    }

    const closeMobileDropdownDialog = (e) => {
      const clickedNavItem = e.target.closest("[data-component='mobile-dropdown-nav-item']");
      const mobileDropdownDialog = clickedNavItem.querySelector("[data-component='mobile-dropdown-dialog']");
      mobileDropdownDialog.close();
    }

    const closeBothMobileDialogs = (e) => {
      const clickedNavItem = e.target.closest("[data-component='mobile-dropdown-nav-item']");
      const mobileDropdownDialog = clickedNavItem.querySelector("[data-component='mobile-dropdown-dialog']");
      mobileDropdownDialog.close(); // close the dropdown dialog
      this.mobileDialog.close(); // close the main dialog
    }

    const toggleDesktopDialog = (e) => {
      const clickedNavItem = e.target.closest("[data-component='desktop-dropdown-nav-item']");
      const desktopDropdownDialog = clickedNavItem.querySelector("[data-component='desktop-dropdown-dialog']");
      const allDesktopDropdownDialogs = this.desktopNav.querySelectorAll("[data-component='desktop-dropdown-dialog']");

      if (clickedNavItem.classList.contains("open")) {
        desktopDropdownDialog.close();
        clickedNavItem.classList.remove("open");
      }
      else {
        desktopDropdownDialog.show();
        clickedNavItem.classList.add("open");
        removeFocus(desktopDropdownDialog);

        //close all other dropdowns when this one opens
        allDesktopDropdownDialogs.forEach((aDesktopDropdownDialog: HTMLDialogElement) => {
          const correspondingNavItem = aDesktopDropdownDialog.closest("[data-component='desktop-dropdown-nav-item']");
          if (correspondingNavItem !== clickedNavItem) {
            aDesktopDropdownDialog.close();
            correspondingNavItem.classList.remove("open");
          }
        })
      }
    }

    const closeAllDesktopDialogs = () => {
      const desktopDialogs = this.desktopNav.querySelectorAll("[data-component='desktop-dropdown-dialog']");
      desktopDialogs.forEach((desktopDialog: HTMLDialogElement) => {
        desktopDialog.close();
      })
      const desktopDropdownNavItems = this.desktopNav.querySelectorAll("[data-component='desktop-dropdown-nav-item']");
      desktopDropdownNavItems.forEach((desktopDropdownNavItem) => {
        desktopDropdownNavItem.classList.remove("open");
      })
    }

    const handleKeyup = (e) => {
      if (e.key === 'Escape') {
        closeAllDesktopDialogs();
      }
    }

    const handleScroll = () => {
      const st = window.scrollY || document.documentElement.scrollTop; // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"

      if (st > this.lastScrollTop) {
        if (window.scrollY > 50) {
          this.element.classList.remove("sticky-visible");
          this.element.classList.add("sticky-hidden");
        }
      } else if (st < this.lastScrollTop) {
        this.element.classList.remove("sticky-hidden");
        this.element.classList.add("sticky-visible");
      } // else was horizontal scroll
      this.lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling
    }

    const handleResize = () => {
      const currentViewportWidth = window.innerWidth;
      const resizedFromMobileToDesktop = (this.viewportWidth < 1024) && (currentViewportWidth >= 1024);
      const resizedFromDesktopToMobile = (this.viewportWidth >= 1024) && (currentViewportWidth < 1024);

      if (resizedFromMobileToDesktop || resizedFromDesktopToMobile) {
        this.mobileDialog.close();
        this.mobileDropdownDialogs.forEach((mobileDropdownDialog: HTMLDialogElement) => {
          mobileDropdownDialog.close();
        })
        closeAllDesktopDialogs();
      }

      this.viewportWidth = currentViewportWidth;
    }

    if (this.mobileHamburgerBtn) {
      this.mobileHamburgerBtn.addEventListener("click", openMobileDialog);
    }

    if (this.mobileCloseBtn) {
      this.mobileCloseBtn.addEventListener("click", closeMobileDialog);
    }

    this.mobileDropdownBtns.forEach((mobileDropdownBtn) => {
      mobileDropdownBtn.addEventListener("click", openMobileDropdownDialog);
    })

    this.mobileDropdownBackBtns.forEach((mobileDropdownBackBtn) => {
      mobileDropdownBackBtn.addEventListener("click", closeMobileDropdownDialog);
    })

    this.mobileDropdownCloseBtns.forEach((mobileDropdownCloseBtn) => {
      mobileDropdownCloseBtn.addEventListener("click", closeBothMobileDialogs);
    })

    this.desktopDropdownBtns.forEach((desktopDropdownBtn) => {
      desktopDropdownBtn.addEventListener("click", toggleDesktopDialog);
    })

    this.desktopFooter.forEach((desktopFooter) => {
      desktopFooter.addEventListener("click", closeAllDesktopDialogs);
    });

    window.addEventListener("keyup", handleKeyup);
    window.addEventListener("scroll", handleScroll, false);
    window.addEventListener("resize", debounce(handleResize, 100));
  }

  static init() {
    const navbars: Element[] = [...document.querySelectorAll('[data-component="navigation-bar"]')];
    navbars.forEach(navbar => {
      new TpxNavigationBarUtils(navbar as HTMLElement);
    });
  }
}